import {
  normalizeTemperatureComplianceSubtaskId,
  parseTemperatureComplianceReading,
} from '../constants/complianceTemperatureZones';

export const TASK_TYPES = ['dateCheck', 'shortlifeDateCheck', 'produceDateCheck', 'dailyInventory', 'temperatureCheck'];
const FULL_INVENTORY_TASK_ID = 'fullInventory';
const FULL_INVENTORY_TASK_TYPE = 'fullInventoryCount';

export const DEFAULT_TASK_QUOTAS = {
  dateCheck: 30,
  shortlifeDateCheck: 20,
  produceDateCheck: 8,
  dailyInventory: 30,
  temperatureCheck: 0,
};

const normalizeText = (value) => String(value ?? '').trim();

const getLogSortTime = (log = {}) => {
  const candidates = [log.updated_at, log.completed_at, log.created_at];
  let best = 0;
  candidates.forEach((value) => {
    const ts = new Date(value || 0).getTime();
    if (Number.isFinite(ts) && ts > best) best = ts;
  });
  return best;
};

/** One log per (task_type, subtask_id), latest wins — merges all users for the same store/day. */
export const mergeComplianceLogsByLatestSubtask = (logs = []) => {
  const map = new Map();
  (logs || []).forEach((log) => {
    const taskType = String(log.task_type || log.task_id || '');
    if (!TASK_TYPES.includes(taskType)) return;
    if (taskType === FULL_INVENTORY_TASK_TYPE || log.task_id === FULL_INVENTORY_TASK_ID) return;

    const rawSub = String(log.subtask_id || '');
    const subId = taskType === 'temperatureCheck'
      ? normalizeTemperatureComplianceSubtaskId(rawSub)
      : rawSub;
    const key = `${taskType}::${subId}`;
    const prev = map.get(key);
    if (!prev) {
      map.set(key, log);
      return;
    }
    if (getLogSortTime(log) >= getLogSortTime(prev)) {
      map.set(key, log);
    }
  });
  return Array.from(map.values());
};

const hasInventoryDataKeys = (data = {}) => (
  ['shopCount', 'shop_count', 'warehouseCount', 'warehouse_count', 'totalQuantity', 'total_quantity', 'quantity']
    .some((key) => Object.prototype.hasOwnProperty.call(data || {}, key))
);

export const isComplianceSubtaskFilledFromLog = (taskType, log) => {
  if (!log) return false;
  const data = log.data || {};
  switch (taskType) {
    case 'dateCheck':
      return Boolean(normalizeText(data.date)) || Boolean(data.notAvailable);
    case 'shortlifeDateCheck':
      return data.checked === true;
    case 'produceDateCheck':
      return Boolean(normalizeText(data.status)) || Boolean(data.notAvailable);
    case 'dailyInventory':
      return hasInventoryDataKeys(data);
    case 'temperatureCheck':
      return parseTemperatureComplianceReading(data) !== null;
    default:
      return Number(log.subtask_completed || 0) > 0;
  }
};

export const summarizeTaskLogs = (logs = [], taskType, options = {}) => {
  const { expectedSubtaskIds = null } = options;

  const merged = mergeComplianceLogsByLatestSubtask(
    (logs || []).filter((log) => (
      (log.task_type === taskType || log.task_id === taskType)
      && log.task_type !== FULL_INVENTORY_TASK_TYPE
      && log.task_id !== FULL_INVENTORY_TASK_ID
    ))
  );

  const logBySubId = new Map(
    merged.map((entry) => {
      const raw = String(entry.subtask_id || '');
      const key = taskType === 'temperatureCheck'
        ? normalizeTemperatureComplianceSubtaskId(raw)
        : raw;
      return [key, entry];
    })
  );

  let lastUpdated = null;
  merged.forEach((log) => {
    const candidate = log.completed_at || log.updated_at || log.created_at || null;
    if (candidate && (!lastUpdated || candidate > lastUpdated)) {
      lastUpdated = candidate;
    }
  });

  if (Array.isArray(expectedSubtaskIds)) {
    const totalSubtasks = expectedSubtaskIds.length;
    let completedSubtasks = 0;
    expectedSubtaskIds.forEach((rawId) => {
      const id = String(rawId);
      if (isComplianceSubtaskFilledFromLog(taskType, logBySubId.get(id))) {
        completedSubtasks += 1;
      }
    });
    const boundedCompleted = Math.min(completedSubtasks, totalSubtasks);
    const completionPercentage = totalSubtasks > 0
      ? Math.round((boundedCompleted / totalSubtasks) * 10000) / 100
      : 0;

    return {
      taskType,
      completed: totalSubtasks > 0 && boundedCompleted >= totalSubtasks,
      itemCount: logBySubId.size,
      totalSubtasks,
      completedSubtasks: boundedCompleted,
      completionPercentage,
      lastUpdated,
    };
  }

  const bySubtask = new Map();
  merged.forEach((log) => {
    let subtaskKey = log.subtask_id || `${log.task_id || log.task_type}-${log.id}`;
    if (taskType === 'temperatureCheck') {
      subtaskKey = normalizeTemperatureComplianceSubtaskId(String(subtaskKey || ''));
    }
    const filled = isComplianceSubtaskFilledFromLog(taskType, log) ? 1 : 0;
    bySubtask.set(subtaskKey, filled);
  });

  // Fixed quota until we have a real product list — do not use max(quota, bySubtask.size)
  // or the denominator grows with every new log row and completion % jumps erratically.
  const defaultQuota = DEFAULT_TASK_QUOTAS[taskType] ?? 0;
  const totalSubtasks = defaultQuota > 0 ? defaultQuota : Math.max(1, bySubtask.size);
  const completedSubtasks = Array.from(bySubtask.values()).reduce((sum, value) => sum + (value > 0 ? 1 : 0), 0);
  const boundedCompleted = Math.min(completedSubtasks, totalSubtasks);
  const completionPercentage = totalSubtasks > 0
    ? Math.round((boundedCompleted / totalSubtasks) * 10000) / 100
    : 0;

  return {
    taskType,
    completed: totalSubtasks > 0 && boundedCompleted >= totalSubtasks,
    itemCount: bySubtask.size,
    totalSubtasks,
    completedSubtasks: boundedCompleted,
    completionPercentage,
    lastUpdated,
  };
};

export const summarizeDailyCompliance = (logs = [], expectedSubtaskIdsByTask = null) => {
  const tasks = Object.fromEntries(
    TASK_TYPES.map((taskType) => [
      taskType,
      summarizeTaskLogs(logs, taskType, {
        expectedSubtaskIds: expectedSubtaskIdsByTask && Array.isArray(expectedSubtaskIdsByTask[taskType])
          ? expectedSubtaskIdsByTask[taskType]
          : null,
      }),
    ])
  );

  return {
    tasks,
    completedTaskCount: Object.values(tasks).filter((task) => task.completed).length,
    totalTaskCount: TASK_TYPES.length,
  };
};
